import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BackArrow, IconButton, Edit } from 'qampo-ui-library';
import TrashIcon from '../assets/icons/trash.svg?react';
import { Table } from '../components/UI';
import { Button } from '../components/UI/base/Button';
import type { TableColumn } from '../components/UI';
import { InterventionFormModal } from '../components/Configuration/InterventionFormModal';
import { DeleteInterventionModal } from '../components/Configuration/DeleteInterventionModal';
import { useInterventionTemplates } from '../hooks/useInterventionTemplates';
import { useStaffCompetences } from '../hooks/useStaffCompetences';
import { useAppDispatch } from '../store/hooks';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { getErrorMessage } from '../utils/errorMessageUtils';
import {
  formatFlexCompetences,
  formatRequiredCompetences,
  toCreateRequest,
  toUpdateRequest
} from '../services/interventionTemplateMappers';
import ROUTES from '../routes/routeConstants';
import styles from './InterventionsPage.module.css';
import { SessionType, type InterventionTemplateResponse } from '../api/generated';

// Minimum row height (px) for templates whose cells wrap onto multiple lines.
const MULTILINE_ROW_MIN_HEIGHT = 48;

export const InterventionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { templates, isLoading, loadError, createTemplate, updateTemplate, deleteTemplate } =
    useInterventionTemplates();
  const { competences } = useStaffCompetences();

  const handleBack = (): void => {
    navigate(`/${ROUTES.configuration}`);
  };

  const [interventionModalOpen, setInterventionModalOpen] = useState(false);
  const [editingIntervention, setEditingIntervention] =
    useState<InterventionTemplateResponse | null>(null);
  const [deleteConfirmIntervention, setDeleteConfirmIntervention] =
    useState<InterventionTemplateResponse | null>(null);

  const professionNameById = useMemo(
    () => new Map(competences.map((c) => [c.id, c.name])),
    [competences]
  );

  const handleOpenInterventionModal = (
    intervention: InterventionTemplateResponse | null = null
  ): void => {
    setEditingIntervention(intervention);
    setInterventionModalOpen(true);
  };

  const handleSaveIntervention = async (
    intervention: InterventionTemplateResponse
  ): Promise<void> => {
    const isUpdate = editingIntervention !== null;
    try {
      if (isUpdate) {
        await updateTemplate(intervention.id, toUpdateRequest(intervention));
      } else {
        await createTemplate(toCreateRequest(intervention));
      }
      setInterventionModalOpen(false);
      setEditingIntervention(null);
    } catch (e) {
      const fallback = isUpdate ? t('config-iv-update-error') : t('config-iv-create-error');
      dispatch(showError({ message: getErrorMessage(e, fallback) }));
    }
  };

  const handleDeleteIntervention = (intervention: InterventionTemplateResponse): void => {
    setDeleteConfirmIntervention(intervention);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteConfirmIntervention) return;
    const idToDelete = deleteConfirmIntervention.id;
    setDeleteConfirmIntervention(null);
    try {
      await deleteTemplate(idToDelete);
    } catch (e) {
      dispatch(showError({ message: getErrorMessage(e, t('config-iv-delete-error')) }));
    }
  };

  const columns = useMemo<TableColumn<InterventionTemplateResponse>[]>(() => [
    {
      key: 'name',
      header: t('config-iv-col-name'),
      render: (iv) => (
        <div className={styles.ivNameCell}>
          <span className={styles.ivColorBar} style={{ backgroundColor: iv.color }} />
          {iv.name}
        </div>
      )
    },
    {
      key: 'duration',
      header: t('config-iv-col-duration'),
      width: 120,
      render: (iv) => `${iv.durationMinutes} ${t('room-booking-minutes-suffix')}`
    },
    {
      key: 'sessionType',
      header: t('config-iv-col-session-type'),
      width: 140,
      render: (iv) => {
        const badgeMod =
          iv.sessionType === SessionType.Group
            ? styles.badgeGroup
            : iv.sessionType === SessionType.Internal
              ? styles.badgeInternal
              : styles.badgeIndividual;
        const label =
          iv.sessionType === SessionType.Group
            ? t('config-iv-group')
            : iv.sessionType === SessionType.Internal
              ? t('config-iv-internal')
              : t('config-iv-individual');
        return <span className={`${styles.badge} ${badgeMod}`}>{label}</span>;
      }
    },
    {
      key: 'rooms',
      header: t('config-iv-col-rooms'),
      render: (iv) => (
        <div className={styles.roomList}>
          {iv.roomRequirements.map((r) => (
            <span
              key={r.id}
              className={`${styles.roomTag} ${r.name === 'Online' ? styles.roomTagOnline : ''}`}
            >
              {r.name}
            </span>
          ))}
        </div>
      )
    },
    {
      key: 'skills',
      header: t('config-iv-col-skills'),
      render: (iv) => {
        const flexSummary = formatFlexCompetences(iv, professionNameById, t);
        return (
          <div className={styles.skillsCell}>
            <span>{formatRequiredCompetences(iv, professionNameById)}</span>
            {flexSummary && <span className={styles.flexLine}>{flexSummary}</span>}
          </div>
        );
      }
    },
    {
      key: 'actions',
      header: '',
      width: 100,
      headerAlign: 'center',
      render: (iv) => (
        <div className={styles.rowActions}>
          <IconButton
            size="small"
            icon={<Edit width={14} height={14} />}
            onClick={() => handleOpenInterventionModal(iv)}
            classes={styles.actionBtn}
          />
          <IconButton
            size="small"
            icon={<TrashIcon width={14} height={14} />}
            onClick={() => handleDeleteIntervention(iv)}
            classes={`${styles.actionBtn} ${styles.actionBtnDelete}`}
          />
        </div>
      )
    }
  ], [t, professionNameById]);

  const isInitialLoading = isLoading && templates.length === 0;
  const isEmptyState = !isLoading && !loadError && templates.length === 0;

  return (
    <main className={styles.page}>
      <div className={styles.topBar}>
        <nav className={styles.breadcrumbRow} aria-label={t('breadcrumb')}>
          <IconButton icon={<BackArrow />} onClick={handleBack} />
          <div className={styles.breadcrumbStack}>
            <button type="button" className={styles.breadcrumbLink} onClick={handleBack}>
              {t('configuration')}
            </button>
            <span className={styles.breadcrumbCurrent}>{t('config-tab-interventions')}</span>
          </div>
        </nav>
        <Button type="primary" onClick={() => handleOpenInterventionModal(null)}>
          {t('config-add-intervention')}
        </Button>
      </div>

      {isInitialLoading && <p className={styles.emptyState}>{t('config-iv-loading')}</p>}

      {loadError && <p className={styles.emptyState}>{t('config-iv-load-error')}</p>}

      {isEmptyState && <p className={styles.emptyState}>{t('config-iv-empty')}</p>}

      {templates.length > 0 && (
        <Table
          columns={columns}
          data={templates}
          getRowKey={(iv) => iv.id}
          minWidth={960}
          getRowStyles={(iv) =>
            iv.roomRequirements.length > 1 ||
            iv.mandatoryStaffCompetenceRequirements.length > 1 ||
            iv.flexStaffCompetenceRequirements.length > 0
              ? { height: undefined, minHeight: MULTILINE_ROW_MIN_HEIGHT }
              : {}
          }
        />
      )}

      {interventionModalOpen && (
        <InterventionFormModal
          intervention={editingIntervention}
          onSave={handleSaveIntervention}
          onClose={() => {
            setInterventionModalOpen(false);
            setEditingIntervention(null);
          }}
        />
      )}

      {deleteConfirmIntervention && (
        <DeleteInterventionModal
          interventionName={deleteConfirmIntervention.name}
          onConfirm={handleConfirmDelete}
          onClose={() => setDeleteConfirmIntervention(null)}
        />
      )}
    </main>
  );
};
